import { DefinedInitialDataOptions, useQuery } from '@tanstack/react-query';
import { createSelector } from 'reselect';
import ApiService from 'src/services/ApiService';
import { DocumentType } from 'src/services/documents/types';
import { getQueryClient } from 'src/utils/queryClientRegistry';

const selectDocumentTypesObjById = createSelector(
  [(documentTypes: DocumentType[]) => documentTypes],
  (documentTypes) => {
    if (!documentTypes)
      return {
        documentTypes: [],
        documentTypesObj: {},
      };
    const documentTypesObj: Record<string, DocumentType> = {};
    documentTypes.forEach((type) => (documentTypesObj[type.id] = type));
    return {
      documentTypes,
      documentTypesObj,
    };
  },
);

export const checkDocumentTypesCache = (
  documents: {
    documentTypeId?: string | null;
    documentType?: DocumentType | null;
  }[],
) => {
  const queryClient = getQueryClient();
  if (!queryClient) return;

  const docTypesQueryKey = [
    '/document-types',
    { getAllDocumentTypes: false, getFields: false },
  ];
  const cachedTypes =
    queryClient.getQueryData<DocumentType[]>(docTypesQueryKey);

  if (!cachedTypes || cachedTypes.length === 0) return;

  const typeIds = new Set(cachedTypes.map((t) => t.id));
  const hasMissingType = documents.some(
    (doc) =>
      doc.documentTypeId &&
      !typeIds.has(doc.documentTypeId) &&
      // Documents that carry their own embedded type don't need the cache
      // refreshed — avoids churn for docs assigned to a withheld isDeleted type.
      !doc.documentType,
  );

  if (hasMissingType) {
    queryClient.invalidateQueries({ queryKey: ['/document-types'] });
  }
};

export const useDocumentTypes = <SelectType = DocumentType[]>(
  {
    getAllDocumentTypes = false,
    getFields = false,
  }: { getAllDocumentTypes?: boolean; getFields?: boolean } = {},
  options?: Partial<
    DefinedInitialDataOptions<
      DocumentType[],
      Error,
      SelectType,
      ['/document-types', { getAllDocumentTypes: boolean; getFields: boolean }]
    >
  >,
) => {
  return useQuery({
    // Provide empty placeholder data so query never blocks - components can render immediately
    placeholderData: (previousData) => previousData ?? [],
    ...options,
    queryKey: ['/document-types', { getAllDocumentTypes, getFields }] as const,
    queryFn: ApiService.documents.getDocumentTypes,
    select: selectDocumentTypesObjById,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};
